import { AnyFieldApi } from '@tanstack/react-form-nextjs'
import { CircleAlertIcon } from 'lucide-react'

import { FieldInfo } from '@/components/form/field-info'
import { Label } from '@/components/ui/label'

import { cn, isFieldInvalid } from '@/utils'

import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'

export type FieldWrapperClasses = {
  childrenWrapper?: string
  label?: string
  wrapper?: string
}

export type FieldWrapperClassNames = {
  classNames?: FieldWrapperClasses
}

export type FieldWrapperProps = {
  classNames?: FieldWrapperClasses
  field: AnyFieldApi
  label?: React.ReactNode
  labelTooltip?: string
  required?: boolean
  reverse?: boolean
  showFieldInfo?: boolean
} & React.HTMLAttributes<HTMLDivElement> &
  React.PropsWithChildren
export const FieldWrapper: React.FC<FieldWrapperProps> = ({
  children,
  classNames,
  field,
  label,
  labelTooltip,
  required,
  reverse,
  showFieldInfo = true,
  ...props
}) => {
  const isInvalid = isFieldInvalid(field)
  // A "\n" in a translated label separates the label from a longer explanation.
  const [labelTitle, ...labelHelp] =
    typeof label === 'string' ? label.split('\n') : [label]
  return (
    <div
      className={cn(
        label
          ? 'flex flex-col gap-1 lg:flex-row lg:items-center'
          : 'flex flex-col gap-1',
        reverse && '!flex-row-reverse',
        props.className,
        classNames?.wrapper
      )}
      data-invalid={isInvalid || undefined}
      {...props}
    >
      {label && (
        <Label
          className={cn(
            'flex w-full flex-shrink-0 items-center gap-2 lg:w-[var(--field-label-width,calc(100%/6))] lg:pe-4',
            classNames?.label
          )}
          htmlFor={field.name}
        >
          <span className="flex flex-col gap-0.5">
            <span>
              {labelTitle}
              {required && <span className="text-destructive ms-1">*</span>}
            </span>
            {labelHelp.length > 0 && (
              <span className="text-muted-foreground text-xs leading-snug font-normal">
                {labelHelp.join(' ')}
              </span>
            )}
          </span>
          {labelTooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <CircleAlertIcon className="text-foreground" size={16} />
              </TooltipTrigger>
              <TooltipContent>
                <p>{labelTooltip}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </Label>
      )}
      <div
        className={cn(
          'flex flex-1 flex-col gap-2',
          classNames?.childrenWrapper,
          isInvalid && '**:border-destructive'
        )}
        id={field.name}
      >
        {children}
        {showFieldInfo ? <FieldInfo field={field} /> : null}
      </div>
    </div>
  )
}
